
/**
 * @fileoverview Componente molecule QrPreview para previsualizar mensajes QR en formato chat simulado.
 * Permite mostrar archivos adjuntos y texto de plantilla en una vista compacta y estilizada.
 * Optimizado con React.memo y useMemo para performance.
 *
 * @module QrPreview
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa, optimización de memoización y validaciones.
 */

/**
 * @typedef {Object} QrPreviewProps
 * @description Propiedades del componente QrPreview (sin props directos, usa contexto de formulario).
 */

/**
 * Componente molecule para previsualizar mensajes QR en formato chat simulado.
 *
 * @component QrPreview
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Muestra archivo adjunto y texto de plantilla en vista de chat
 * - Utiliza react-hook-form para obtener datos del formulario
 * - Optimizado con React.memo y useMemo para evitar renders innecesarios
 * - Estilizado con TailwindCSS (.tw-wrapper)
 * - Traducción con i18n
 *
 * **Atomic Design:**
 * - Nivel: Molecule
 * - Composición: Usa PreviewFile (atom) y utilidades de formulario
 * - Dependencias: PreviewFile, useTranslation, useFormContext
 *
 * @returns {JSX.Element|null} Elemento renderizado con la previsualización o null si no hay datos
 *
 * @example
 * // Uso dentro de un formulario react-hook-form
 * <FormProvider ...>
 *   <QrPreview />
 * </FormProvider>
 *
 * @see {@link ../../../components/PreviewFile} Componente para mostrar archivos adjuntos
 * @see {@link useFormContext} Contexto de formulario react-hook-form
 *
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización
 */

import React, { memo, useMemo } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PreviewFile from '../../../components/PreviewFile';

/**
 * Memoiza y selecciona el archivo adjunto principal.
 * @function selectedFile
 * @memberof QrPreview
 * @private
 * @param {Array} attachedFile - Array de archivos adjuntos
 * @returns {Object|null} Primer archivo adjunto o null
 */

/**
 * Memoiza la condición para mostrar la previsualización.
 * @function shouldShowPreview
 * @memberof QrPreview
 * @private
 * @param {Object|null} selectedFile - Archivo adjunto seleccionado
 * @param {string} templateText - Texto de plantilla
 * @returns {boolean} true si hay archivo o texto, false si no
 */

const QrPreview = memo(() => {
  const { t } = useTranslation('NewChat');
  const { control } = useFormContext();

  const attachedFile = useWatch({
    control,
    name: 'attachedFile'
  }) || [];

  const templateText = useWatch({
    control,
    name: 'templateText'
  });

  const selectedFile = useMemo(() => {
    return Array.isArray(attachedFile) && attachedFile.length > 0 
      ? attachedFile[0] 
      : null;
  }, [attachedFile]);

  const shouldShowPreview = useMemo(() => {
    return !!(selectedFile || templateText);
  }, [selectedFile, templateText]);


  /**
   * Retorno temprano optimizado: no renderiza si no hay datos para mostrar.
   * @memberof QrPreview
   */
  if (!shouldShowPreview) {
    return null;
  }


  /**
   * Renderiza la previsualización del mensaje QR en formato chat.
   * @memberof QrPreview
   * @returns {JSX.Element}
   */
  return (
    <div className="tw-wrapper">
      <div className="tw-space-y-4">
        <h4 className="tw-font-medium tw-my-4">
          {t('qrMessage.messageExample')}
        </h4>

        <div className="tw-flex tw-justify-center">
          <div className="tw-bg-green-100 tw-rounded-lg tw-max-w-sm tw-text-gray-800 tw-text-sm tw-leading-6">
            {selectedFile && (
              <div className="tw-pt-1 tw-px-1">
                <PreviewFile selectedFile={selectedFile} />
              </div>
            )}
            {templateText && (
              <div className="tw-p-3">
                {templateText}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});


/**
 * Nombre de display para el componente QrPreview.
 * @type {string}
 */
QrPreview.displayName = 'QrPreview';

export default QrPreview;